import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { useOnboarding } from '../contexts/OnboardingContext';
import type { TTourAnchor, TTourAnchorRect } from '../types';
import { registerTourAnchor } from '../utils/anchors';
import { SpotlightTourContext } from '../utils/spotlightContext';
import { clampToTourBounds, subscribeTourTooltipHeight, tourSpotBounds } from '../utils/viewport';

const MEASURE_TIMEOUT = 500;

const measureNode = (node: View | null): Promise<TTourAnchorRect | null> =>
  new Promise(resolve => {
    if (!node) {
      resolve(null);
      return;
    }

    const timeout = setTimeout(() => resolve(null), MEASURE_TIMEOUT);

    node.measureInWindow((x, y, width, height) => {
      clearTimeout(timeout);

      const isMeasured = [x, y, width, height].every(
        value => typeof value === 'number' && !Number.isNaN(value),
      );

      resolve(isMeasured ? { x, y, width, height } : null);
    });
  });

type TItemHandle = {
  measure: () => Promise<TTourAnchorRect | null>;
};

const TourAnchorItemsContext = createContext<((handle: TItemHandle) => () => void) | null>(null);

export const TourAnchorItem = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<View>(null);
  const register = useContext(TourAnchorItemsContext);

  useEffect(() => {
    if (!register) {
      return;
    }

    return register({ measure: () => measureNode(ref.current) });
  }, [register]);

  if (!register) {
    return <>{children}</>;
  }

  return (
    <View ref={ref} collapsable={false}>
      {children}
    </View>
  );
};

type TTourAnchorProps = {
  anchor?: TTourAnchor;
  children: React.ReactNode;
  fill?: boolean;
  style?: StyleProp<ViewStyle>;
};

export const TourAnchor = ({ anchor, children, fill = true, style }: TTourAnchorProps) => {
  const ref = useRef<View>(null);
  const { steps } = useOnboarding();
  const { changeSpot, current } = useContext(SpotlightTourContext);
  const items = useRef(new Set<TItemHandle>()).current;

  useEffect(() => {
    if (!anchor) {
      return;
    }

    return registerTourAnchor(anchor, { measure: () => measureNode(ref.current) });
  }, [anchor]);

  const registerItem = useCallback(
    (handle: TItemHandle) => {
      items.add(handle);

      return () => {
        items.delete(handle);
      };
    },
    [items],
  );

  const isCurrent = useMemo(
    () => current !== undefined && steps[current]?.target === anchor,
    [steps, current, anchor],
  );

  const updateSpot = useCallback(async () => {
    if (!anchor || !isCurrent) {
      return;
    }

    const [rect, measured] = await Promise.all([
      measureNode(ref.current),
      Promise.all([...items].map(item => item.measure())),
    ]);

    if (rect) {
      changeSpot(
        clampToTourBounds(
          rect,
          tourSpotBounds(anchor),
          measured.filter((item): item is TTourAnchorRect => !!item),
        ),
      );
    }
  }, [anchor, isCurrent, changeSpot, items]);

  useEffect(() => {
    updateSpot();

    return subscribeTourTooltipHeight(() => {
      updateSpot();
    });
  }, [updateSpot]);

  if (!anchor) {
    return <>{children}</>;
  }

  return (
    <TourAnchorItemsContext.Provider value={registerItem}>
      <View
        ref={ref}
        collapsable={false}
        onLayout={() => updateSpot()}
        style={[fill ? styles.fill : styles.hug, style]}
      >
        {children}
      </View>
    </TourAnchorItemsContext.Provider>
  );
};

const styles = StyleSheet.create({
  fill: { alignSelf: 'stretch' },
  hug: { alignSelf: 'flex-start' },
});

export default TourAnchor;
